/**
 * WO#3 — Owner-level territory read model (Dashboard / My Territories).
 *
 * PURE composition. This file introduces NO new mathematics: adjacency,
 * union-area, GP capacity, publishability threshold and wallet identity all
 * remain the WO#1 authority. It only shapes the authoritative derived
 * territories into a deterministic read model for the Dashboard.
 *
 * Territory is DERIVED STATE. The `key` below is an EPHEMERAL, deterministic
 * UI/render key derived from the underlying property-unit identity set. It is
 * NOT a persisted, on-chain, or database territory identifier and must never be
 * presented or stored as one.
 */
import {
  buildTerritories,
  normalizeOwnerIdentity,
  type PropertyUnit,
} from "./territory";
import { isPublishable } from "./grid-power";
import { apiParcelsToPropertyUnits } from "./live-territory-adapter";
import type { ApiParcel } from "../lib/parcels-by-owner";

export interface TerritoryBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface OwnerTerritory {
  /** EPHEMERAL deterministic render/nav key — NOT a persisted territory ID. */
  key: string;
  /** Canonically normalized owner identity (WO#1). */
  owner: string;
  /** Total union area in world units² (WO#1). */
  area: number;
  /** GP capacity derived from union area (WO#1, tier-independent). */
  capacity: number;
  /** Publishable per the WO#1 threshold. */
  publishable: boolean;
  /** Number of underlying property units in the connected component. */
  unitCount: number;
  /** Authoritative integer unit IDs, sorted ascending (ApiParcel.id is integer). */
  unitIds: number[];
  /** Presentation-only bounds derived from the real underlying units. */
  bounds: TerritoryBounds;
}

/**
 * Deterministic, collision-safe ephemeral key for a derived territory.
 * ApiParcel.id is authoritatively an integer, so we sort numerically and
 * serialize the canonical set with JSON (unambiguous — no separator ambiguity).
 */
export function deriveTerritoryKey(
  normalizedOwner: string,
  unitIds: readonly number[],
): string {
  const sorted = [...unitIds].sort((a, b) => a - b);
  return `t:${normalizedOwner}:${JSON.stringify(sorted)}`;
}

function boundsOf(units: readonly PropertyUnit[]): TerritoryBounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const u of units) {
    if (u.x < minX) minX = u.x;
    if (u.y < minY) minY = u.y;
    if (u.x + u.w > maxX) maxX = u.x + u.w;
    if (u.y + u.h > maxY) maxY = u.y + u.h;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Resolve ALL complete continuous territories owned by `owner` from ONE
 * complete authoritative owner-parcel dataset. Deterministic ordering:
 * min world Y, then min world X, then minimum unit ID.
 */
export function resolveOwnerTerritories(
  parcels: readonly ApiParcel[],
  owner: string,
): OwnerTerritory[] {
  const normalizedOwner = normalizeOwnerIdentity(owner);
  const units = apiParcelsToPropertyUnits(parcels);
  const territories = buildTerritories(units).filter(
    (t) => t.owner === normalizedOwner,
  );

  const models: OwnerTerritory[] = territories.map((t) => {
    const unitIds = t.units.map((u) => Number(u.id)).sort((a, b) => a - b);
    const bounds = boundsOf(t.units);
    return {
      key: deriveTerritoryKey(normalizedOwner, unitIds),
      owner: t.owner,
      area: t.area,
      capacity: t.capacity,
      publishable: isPublishable(t.area),
      unitCount: t.units.length,
      unitIds,
      bounds,
    };
  });

  models.sort((a, b) => {
    if (a.bounds.minY !== b.bounds.minY) return a.bounds.minY - b.bounds.minY;
    if (a.bounds.minX !== b.bounds.minX) return a.bounds.minX - b.bounds.minX;
    return a.unitIds[0] - b.unitIds[0];
  });

  return models;
}
